import { useEffect, useState } from "react";
import { FiImage, FiUpload, FiX } from "react-icons/fi";
import { apiRequest } from "../../services/api";
import AgreementDefaults from './AgreementDefaults';

const toSlug = (value) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const getImageUrl = (storageKey) => {
  if (!storageKey) return "";

  if (
    storageKey.startsWith("http://") ||
    storageKey.startsWith("https://")
  ) {
    return storageKey;
  }

  const baseUrl = (
    process.env.REACT_APP_ASSET_URL ||
    process.env.REACT_APP_API_URL ||
    ""
  ).replace(/\/$/, "");

  return `${baseUrl}${storageKey.startsWith("/") ? "" : "/"}${storageKey}`;
};

export default function BrandModal({
  open,
  onClose,
  onSave,
  brand = null,
  accessToken,
}) {
  const [form, setForm] = useState({
    name: "",
    code: "",
    description: "",
    status: "Active",
    logoKey: "",
  });

  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  useEffect(() => {
    if (!open) return;

    setError("");

    setForm({
      name: brand?.name || "",
      code: brand?.code || "",
      description: brand?.description || "",
      status: brand?.isActive === false ? "Inactive" : "Active",
      logoKey: brand?.logoKey || "",
      agreementType: brand?.agreementType || '',
    });
  }, [open, brand]);

  if (!open) return null;

  const handleLogoChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    try {
      setIsUploadingLogo(true);
      setError("");

      const formData = new FormData();
      formData.append("file", file);

      const uploaded = await apiRequest("/uploads/brands", {
        method: "POST",
        body: formData,
        accessToken,
      });

      setForm((previous) => ({
        ...previous,
        logoKey: uploaded.storageKey,
      }));
    } catch (requestError) {
      setError(requestError.message || "Unable to upload the brand logo.");
    } finally {
      setIsUploadingLogo(false);
      event.target.value = "";
    }
  };

  const removeLogo = () => {
    setForm((previous) => ({
      ...previous,
      logoKey: "",
    }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.code.trim()) {
      setError("Brand name and code are required.");
      return;
    }

    if (!form.logoKey) {
      setError("Please upload a brand logo before saving.");
      return;
    }

    try {
      setIsSaving(true);
      setError("");

      await onSave({
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        slug: toSlug(form.name),
        description: form.description.trim() || undefined,
        logoKey: form.logoKey,
        agreementType: form.agreementType || '',
        isActive: form.status === "Active",
      });

      onClose();
    } catch (requestError) {
      const message = requestError?.details?.message || requestError.message;

      setError(
        Array.isArray(message)
          ? message.join(", ")
          : message || "Unable to save brand.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const logoUrl = getImageUrl(form.logoKey);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-8 py-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {brand ? "Edit Brand" : "Add Brand"}
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Create and manage fashion brands.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={isSaving || isUploadingLogo}
            className="flex h-10 w-10 items-center justify-center rounded-xl hover:bg-gray-100 disabled:opacity-50"
            aria-label="Close modal"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="space-y-6 p-8">
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
              {error}
            </div>
          )}

          <div>
            <div className="mb-3 flex items-center justify-between">
              <label className="block text-sm font-semibold text-gray-700">
                Brand Logo *
              </label>

              {form.logoKey && (
                <button
                  type="button"
                  onClick={removeLogo}
                  disabled={isUploadingLogo}
                  className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                >
                  Remove logo
                </button>
              )}
            </div>

            <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
              <div className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="Brand logo preview"
                    className="h-full w-full object-contain p-2"
                  />
                ) : (
                  <FiImage size={30} className="text-gray-400" />
                )}
              </div>

              <div>
                <label
                  className={`inline-flex cursor-pointer items-center gap-2 rounded-xl border px-5 py-3 font-medium transition ${
                    isUploadingLogo
                      ? "cursor-wait border-primary-200 bg-primary-50 text-primary-700"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <FiUpload />

                  {isUploadingLogo
                    ? "Uploading logo…"
                    : logoUrl
                      ? "Replace Logo"
                      : "Upload Logo"}

                  <input
                    type="file"
                    hidden
                    accept="image/jpeg,image/png,image/webp"
                    disabled={isUploadingLogo}
                    onChange={handleLogoChange}
                  />
                </label>

                <p className="mt-2 max-w-sm text-xs leading-5 text-gray-400">
                  JPG, PNG, or WEBP only. Maximum 5 MB. Brand logo is required.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Brand Name *
              </label>

              <input
                type="text"
                placeholder="Nike"
                value={form.name}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    name: event.target.value,
                  }))
                }
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Brand Code *
              </label>

              <input
                type="text"
                placeholder="NIKE"
                value={form.code}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    code: event.target.value.toUpperCase(),
                  }))
                }
                className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
              />
            </div>
          </div>

          <AgreementDefaults value={form.agreementType} onChange={value => setForm(previous => ({ ...previous, agreementType: value }))} />
          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700">
              Description
            </label>

            <textarea
              rows={4}
              placeholder="Enter brand description..."
              value={form.description}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  description: event.target.value,
                }))
              }
              className="w-full resize-none rounded-xl border border-gray-200 p-4 outline-none focus:border-primary-700"
            />
          </div>

          <div>
            <label className="mb-3 block text-sm font-semibold text-gray-700">
              Status
            </label>

            <div className="flex gap-3">
              {["Active", "Inactive"].map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() =>
                    setForm((previous) => ({
                      ...previous,
                      status,
                    }))
                  }
                  className={`rounded-xl border px-5 py-2.5 font-medium transition-all ${
                    form.status === status
                      ? "border-primary-700 bg-primary-700 text-white"
                      : "border-gray-200 bg-white text-gray-700"
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-100 px-8 py-5">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving || isUploadingLogo}
            className="rounded-xl border border-gray-200 px-5 py-3 font-medium disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving || isUploadingLogo}
            className="rounded-xl bg-primary-700 px-6 py-3 font-medium text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSaving ? "Saving…" : brand ? "Update Brand" : "Save Brand"}
          </button>
        </div>
      </div>
    </div>
  );
}
